import { useEffect, useRef } from 'react'
import { postAgentBusy } from '../transport/api'
import { shouldScheduleRetry } from '../transport/reconnect'

/** How often to re-assert. Comfortably inside the server's 4s TTL. */
/** How long a keystroke counts as "still typing" after it lands. */
const TYPING_GRACE_MS = 2500

/**
 * Tell the edge this agent's owner is mid-input, so messages wait instead of landing
 * in the middle of a sentence.
 *
 * The problem is the same one that made a prompt dangerous: an inbound message is
 * INJECTED into the pane and submitted. Arriving while someone is typing, it lands in
 * their half-written line — and the server cannot see that coming, because composer
 * text never reaches the pty until it is sent, and the microphone is a browser state.
 * `needs_input` the server detects; this it has to be told.
 *
 * TYPING IS INFERRED FROM KEYSTROKES, NOT FOCUS. A composer left focused while
 * somebody reads for ten minutes is not input, and treating it as such would hold
 * every message for that whole time — the shape of a stuck latch, arrived at honestly.
 * Two and a half seconds after the last key, the claim lapses on its own.
 *
 * SPEAKING is whenever the microphone is open, on the owner's ruling — not only while
 * speech is actually streaming, which was the narrower option. That decision is safer
 * than it was when it was first discussed, and for a reason outside this file: with a
 * queue behind it, forgetting to close the mic no longer means an agent goes silently
 * unreachable, it means messages wait with a visible reason. The same choice is not
 * the same choice before and after the queue exists.
 */
export function useBusyHeartbeat({
  session,
  host,
  typedAt,
}: {
  /** The open agent, or null when none is. */
  session: string | null
  host: string | null
  /** Timestamp of the last keystroke in the composer; 0 when nothing typed yet. */
  typedAt: number
}) {
  // Read through a ref so the interval never restarts on a keystroke: re-creating the
  // timer on every character would reset the phase and, at speed, could starve it.
  const latest = useRef({ session, host, typedAt })
  /** The claim last announced, so an unchanged one is not repeated. */
  const announced = useRef<string | null>(null)
  latest.current = { session, host, typedAt }

  useEffect(() => {
    if (!session) return
    let alive = true

    const beat = () => {
      const s = latest.current
      if (!alive || !s.session) return
      /*
       * Nothing while the page is hidden, and on a phone that is most of the time.
       * Android's WebView keeps timers running when backgrounded — `KeepRunning`
       * defaults true and `pauseTimers()` is in the branch that only runs when it is
       * false — so without this a phone in a pocket would beat every two seconds
       * forever. `shouldScheduleRetry` is the same predicate the reconnect loops use;
       * one rule, so the two cannot disagree about what "in the background" means.
       *
       * Safe to stop rather than latch: the claim expires 4s later, which resumes
       * delivery. A backgrounded app is also not one anybody is typing into.
       */
      if (!shouldScheduleRetry()) return
      const typing = s.typedAt > 0 && Date.now() - s.typedAt < TYPING_GRACE_MS
      /*
       * 'typing' is now the only claim this can make. There was a 'speaking' one too,
       * which won when both were true because a microphone is the state a user cannot
       * pause — it went with the voice stack on 2026-09-02. The wire still ACCEPTS
       * 'speaking'; nothing in this app sends it, and a future voice feature restores
       * the branch rather than inventing a new vocabulary.
       */
      const reason = typing ? 'typing' : null
      // Announce transitions only; repeating an unchanged claim is the thing removed.
      if (reason === announced.current) return
      announced.current = reason
      if (!reason) return
      // Fire and forget. A failed heartbeat means messages flow again, which is the
      // direction this is allowed to fail in — surfacing an error for it would put a
      // warning on screen about something the user did not ask for and cannot fix.
      /*
       * Where this claim actually lands, and where it does not.
       *
       * `/agent/busy` exists and is load-bearing — avengents-server's delivery gate
       * asks it first, because the words in a browser input box have not reached the
       * terminal yet, so nothing on their side can see them. But it belongs to the
       * edge, and this app proxies only /auth and /account: here the POST 404s and
       * the catch below eats it. The code being present is NOT the feature being
       * present, and that difference is invisible from this side.
       *
       * The replacement is a hold frame on /account/events, which needs the agent's
       * channel_hash. The roster carries it now; the panel that owns the input box
       * does not yet, so this is a stated gap rather than a guess at an identifier —
       * a hold sent under the wrong name would be accepted, receipted without it, and
       * silently protect nobody.
       *
       * 2026-08-18: the TYPING half is closed elsewhere — TerminalSocket.claimTyping
       * rides the pty pipe (which reaches the edge that holds the gate) on every
       * terminal keystroke and composer keystroke in terminal mode. What remains
       * unclosed here is SPEAKING: the microphone is browser state with no terminal
       * traffic to ride, so it still needs the /account/events hold above.
       */
      void postAgentBusy(s.session, reason, s.host).catch(() => {})
    }

    beat()
    /*
     * Claim on CHANGE, never on a timer.
     *
     * This renewed a 4s lease every couple of seconds for as long as someone was
     * typing. That is a heartbeat, and no link here may be built on one. The honest
     * replacement is a hold announced when the state changes and released when it
     * ends — a pair the server has to keep, which this topology has no route for:
     * `/agent/busy` does not exist here at all, so the timer was renewing nothing and
     * its removal takes no behaviour with it. Requesting the pushed hold/release.
     */
    return () => {
      alive = false
    }
  }, [session, host])
}
